import React from "react";
import {
    ShieldCheck,
    Users,
    Sparkles,
    MessageCircle,
    Wallet,
    Layers,
    FileText,
} from "lucide-react";

const reasons = [
    {
        icon: ShieldCheck,
        title: "Reliable local service",
        body: "We turn up when we say we will and we tell you straight if anything changes.",
    },
    {
        icon: Users,
        title: "Run by two hardworking teens",
        body: "A small, motivated crew. We take the work seriously and treat every job like it’s our biggest one.",
    },
    {
        icon: Sparkles,
        title: "Neat and tidy results",
        body: "Crisp edges, level hedges, clean paths. We finish the job and leave it tidier than we found it.",
    },
    {
        icon: MessageCircle,
        title: "Clear communication",
        body: "Quick replies, plain English, no chasing. Text, email or call — whichever suits you.",
    },
    {
        icon: Wallet,
        title: "Fair pricing",
        body: "Honest per-job or hourly rates with no surprise add-ons. You see the price before we lift a tool.",
    },
    {
        icon: Layers,
        title: "Flexible job sizes",
        body: "One mow, a weekend cleanup, or a regular tidy — pick the rhythm that suits the property.",
    },
    {
        icon: FileText,
        title: "Easy quote process",
        body: "Send a few photos and a suburb — we reply within a day with a clear price and a window.",
    },
];

export default function WhyChooseUs() {
    return (
        <section
            id="why"
            data-testid="section-why"
            className="relative bg-[var(--ink)] text-[var(--paper)] py-28 md:py-36 grain"
        >
            <div className="maxw section-pad relative z-10">
                <div className="grid md:grid-cols-12 gap-12 items-end mb-14">
                    <div className="md:col-span-7 reveal">
                        <div className="eyebrow-light mb-5">Why choose FreshEdge</div>
                        <h2 className="font-display font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight">
                            The boring promises that{" "}
                            <span className="italic text-[var(--terra-soft)]">
                                actually matter
                            </span>
                            .
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:text-right">
                        <p className="text-[rgba(242,237,227,0.78)] text-[15px] leading-relaxed max-w-md md:ml-auto">
                            We grew the business on referrals from neighbours.
                            That only works if every job stands up.
                        </p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-[var(--line-dark)] border border-line-dark rounded-2xl overflow-hidden">
                    {reasons.map((r, i) => {
                        const Icon = r.icon;
                        return (
                            <article
                                key={r.title}
                                data-testid={`why-card-${i}`}
                                className="reveal bg-[var(--ink)] p-7 md:p-8 flex flex-col gap-5 min-h-[230px] group hover:bg-[var(--ink-2)] transition-colors"
                                style={{ transitionDelay: `${i * 70}ms` }}
                            >
                                <span className="w-11 h-11 rounded-xl bg-[rgba(92,125,58,0.18)] text-[var(--terra-soft)] flex items-center justify-center group-hover:bg-[var(--terra-soft)] group-hover:text-[var(--ink)] transition-colors">
                                    <Icon size={18} strokeWidth={1.7} />
                                </span>
                                <h3 className="font-display text-xl md:text-[22px] font-medium tracking-tight">
                                    {r.title}
                                </h3>
                                <p className="text-[14px] leading-relaxed text-[rgba(242,237,227,0.78)]">
                                    {r.body}
                                </p>
                            </article>
                        );
                    })}

                    {/* 8th tile to balance the grid */}
                    <article
                        data-testid="why-card-cta"
                        className="reveal bg-[var(--ink-2)] p-7 md:p-8 flex flex-col gap-5 min-h-[230px] justify-between"
                        style={{ transitionDelay: `${reasons.length * 70}ms` }}
                    >
                        <div className="eyebrow-light">Ready to book?</div>
                        <div>
                            <h3 className="font-display text-2xl tracking-tight leading-tight">
                                Send us the job. We’ll get back within a day.
                            </h3>
                            <a
                                href="#contact"
                                className="mt-5 inline-flex items-center gap-2 text-[13.5px] font-medium text-[var(--terra-soft)] hover:text-[var(--paper)] transition"
                                data-testid="why-cta"
                            >
                                Get a free quote →
                            </a>
                        </div>
                    </article>
                </div>
            </div>
        </section>
    );
}
